import { eq, and, or } from 'tio-ui/utilities';
import { fn } from '@ember/helper';
import { service } from '@ember/service';
import { t } from 'ember-intl';
import Component from '@glimmer/component';
import type { TOC } from '@ember/component/template-only';
import type RouterService from '@ember/routing/router-service';
import type AccountModel from 'tio-data/models/account';
import type AccountTransactionModel from 'tio-data/models/account-transaction';
import type MatchParticipantModel from 'tio-data/models/match-participant';
import type MatchPlanModel from 'tio-data/models/match-plan';
import type MatchPaymentModel from 'tio-data/models/match-payment';
import ConfirmationDialog from 'tio-reimagined/components/confirmation-dialog';
import TioPageBreadcrumbs from 'tio-common/components/tio/page/breadcrumbs';
import SyfDashboardInsights from './dashboard/insights';
import SyfDashboardPersonalEmail from './dashboard/personal-email';
import SyfDashboardMatchSummary from './dashboard/match-summary';
import SyfDashboardLoanPayments from './dashboard/loan-payments';
import Checkbox from 'tio-reimagined/components/checkbox';
import Switch from 'tio-reimagined/components/switch';
import Select from 'tio-reimagined/components/select';
import { HStack, Section, VStack } from 'tio-ui/components/layout';

interface S {
  Args: {
    reverseOnMobile?: boolean;
    rightWidthClass?: string;
  };
  Blocks: {
    left: [];
    right?: [];
  };
}

const TwoColumnLayout: TOC<S> = <template>
  <div class="flex flex-col lg:flex-row gap-4">
    {{#if @reverseOnMobile}}
      <div class="lg:flex-1 order-2 lg:order-1">
        {{yield to="left"}}
      </div>
      <div class="order-1 lg:order-2 {{or @rightWidthClass 'lg:w-1/3'}}">
        {{yield to="right"}}
      </div>
    {{else}}
      <div class="lg:flex-1">
        {{yield to="left"}}
      </div>
      <div class={{or @rightWidthClass "lg:w-1/3"}}>
        {{yield to="right"}}
      </div>
    {{/if}}
  </div>
</template>;

interface SyfDashboardSignature {
  Args: {
    accounts: AccountModel[];
    certifiedPayments: MatchPaymentModel[];
    qualifiedPayments: MatchPaymentModel[];
    eligiblePayments: AccountTransactionModel[];
    unverifiedPayments: AccountTransactionModel[];
    unqualifiedPayments: MatchPaymentModel[];
    ineligiblePayments: AccountTransactionModel[];
    onCertifyPayments: (payments: Array<MatchPaymentModel>) => Promise<void>;
    getPaymentQualificationState: (txn: AccountTransactionModel) => string;
    getPaymentCertificationState: (txn: AccountTransactionModel) => string;
    matchParticipant: MatchParticipantModel;
    matchPlan: MatchPlanModel;
    focusedAccount: AccountModel | null;
    showAccountVerifyAlert: boolean;
    showAccountUnverifyAlert: boolean;
    onToggleAccountVerified: (account: AccountModel) => void;
    onCancelAccountIsVerified: () => void;
    accountBorrowerConfirmed: boolean;
    onToggleAccountBorrowerConfirmed: () => void;
    onConfirmAccountIsVerified: (account: AccountModel) => void;
  };
}

interface BenefitYearSelectorSignature {
  Args: {
    matchPlan: MatchPlanModel;
  };
}

type YearOption = { key: string; label: string };

class BenefitYearSelector extends Component<BenefitYearSelectorSignature> {
  @service declare router: RouterService;

  get currentYear() {
    return new Date().getFullYear().toString();
  }

  get selectedYear() {
    const yearFromQuery = this.router.currentRoute?.queryParams?.benefitYear;
    if (typeof yearFromQuery === 'string') {
      return yearFromQuery;
    }
    return this.currentYear;
  }

  get availableYears(): YearOption[] {
    const benefitYears = this.args.matchPlan?.metadata?.benefit_years || {};
    const metadataYears = Object.keys(benefitYears);

    // Always include the current year, even if not yet in metadata
    const currentYearStr = this.currentYear;
    const yearsSet = new Set([currentYearStr, ...metadataYears]);
    const years = Array.from(yearsSet).sort((a, b) => Number(b) - Number(a));

    return years.map((year) => ({ key: year, label: year }));
  }

  get previousYears(): YearOption[] {
    return this.availableYears.filter((year) => year.key !== this.currentYear);
  }

  get hasPreviousYears() {
    return this.previousYears.length > 0;
  }

  yearDidChange = (selectedKey: string) => {
    this.router.transitionTo({ queryParams: { benefitYear: selectedKey } });
  };

  <template>
    <HStack class="items-center justify-between mb-4">
      <h2 class="text-xl font-semibold">
        {{t "syf.dashboard.benefit_year"}}
        {{this.selectedYear}}
      </h2>
      {{#if this.hasPreviousYears}}
        <Select
          @label={{t "syf.dashboard.select_year"}}
          @items={{this.availableYears}}
          @selected={{this.selectedYear}}
          @onChange={{this.yearDidChange}}
          class="w-24"
        />
      {{/if}}
    </HStack>
  </template>
}

// class method to return translation path given state
// pass to t helper in block component, something like t (getPath payment.transactionState)
const SyfDashboardComponent: TOC<SyfDashboardSignature> = <template>
  <div class="pb-8">
    <TioPageBreadcrumbs class="mb-4" as |b|>
      <b.crumb @route="authenticated.syf.dashboard" @label="Dashboard" />
    </TioPageBreadcrumbs>
    <BenefitYearSelector @matchPlan={{@matchPlan}} />
    <TwoColumnLayout>
      <:left>
        <VStack class="p-0!">
          <Section>
            <:header>{{t "syf.dashboard.match_and_loan_payment_details"}}</:header>
            <:body>
              <SyfDashboardMatchSummary
                @qualifiedPayments={{@qualifiedPayments}}
                @certifiedPayments={{@certifiedPayments}}
                @unqualifiedPayments={{@unqualifiedPayments}}
                @matchPlan={{@matchPlan}}
                @onCertifyPayments={{@onCertifyPayments}}
              />
              <SyfDashboardLoanPayments
                @eligiblePayments={{@eligiblePayments}}
                @ineligiblePayments={{@ineligiblePayments}}
                @unverifiedPayments={{@unverifiedPayments}}
                @getPaymentCertificationState={{@getPaymentCertificationState}}
              />
            </:body>
          </Section>
        </VStack>
      </:left>
      <:right>
        <VStack class="p-0!">
          {{#if @matchPlan.showInsights}}
            <SyfDashboardInsights
              @matchParticipant={{@matchParticipant}}
              @matchPlan={{@matchPlan}}
              @accounts={{@accounts}}
            />
          {{/if}}
          <Section>
            <:header>{{t "syf.dashboard.loan_accounts.header"}}</:header>
            <:body>
              <p class="mb-6">{{t "syf.dashboard.loan_accounts.main_content"}}</p>
              <div class="grid grid-cols-2">
                <div class="col-span-1 font-semibold text-center">
                  {{t "syf.dashboard.loan_accounts.account_col_header"}}
                </div>
                <div class="col-span-1 font-semibold text-center">
                  {{t "syf.dashboard.loan_accounts.verified_col_header"}}
                </div>
              </div>
              {{#each @accounts as |account|}}
                <div class="grid grid-cols-2">
                  <div class="col-span-1">
                    {{account.institutionName}}
                  </div>
                  <div class="col-span-1 text-center">
                    <Switch
                      data-test-id="account-verified-switch"
                      @checked={{account.isVerified}}
                      @onChange={{fn @onToggleAccountVerified account}}
                    />
                  </div>
                </div>
                {{! render alert here in order to keep it inside the each loop
          closure so the account var is in scope }}
                <ConfirmationDialog
                  @isOpen={{and (eq @focusedAccount.id account.id) @showAccountVerifyAlert}}
                  @title={{t "syf.dashboard.loan_accounts.confirmation_alert.verify.header"}}
                  @intent="warning"
                  @onConfirm={{fn @onConfirmAccountIsVerified account}}
                  @onCancel={{@onCancelAccountIsVerified}}
                  @isRunning={{account.isSaving}}
                >
                  <p class="mb-6">
                    {{t "syf.dashboard.loan_accounts.confirmation_alert.verify.content"}}
                  </p>
                  <Checkbox
                    {{! font-semibold class here doesn't seem to apply to the label
              text in this Frontile component }}
                    data-test-id="borrower-confirmed-checkbox"
                    class="inline-block m-auto font-semibold"
                    @label={{@matchPlan.ensureVerifyAccountStatement}}
                    @checked={{@accountBorrowerConfirmed}}
                    @onChange={{@onToggleAccountBorrowerConfirmed}}
                  />
                </ConfirmationDialog>
                <ConfirmationDialog
                  @isOpen={{and (eq @focusedAccount.id account.id) @showAccountUnverifyAlert}}
                  @title={{t "syf.dashboard.loan_accounts.confirmation_alert.unverify.header"}}
                  @intent="warning"
                  @onConfirm={{fn @onConfirmAccountIsVerified account}}
                  @onCancel={{@onCancelAccountIsVerified}}
                  @isRunning={{account.isSaving}}
                >
                  <p class="mb-6">
                    {{t "syf.dashboard.loan_accounts.confirmation_alert.unverify.content"}}
                  </p>
                </ConfirmationDialog>
              {{/each}}
            </:body>
          </Section>
          <SyfDashboardPersonalEmail @matchParticipant={{@matchParticipant}} />
        </VStack>
      </:right>
    </TwoColumnLayout>
  </div>
</template>;

export default SyfDashboardComponent;
